import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financialAdvisorProfileApi } from '../api/financialAdvisorProfileApi';
import type { UpdateFinancialAdvisorProfilePayload } from '../types/financial-advisor-profile.types';

export const financialAdvisorProfileKeys = {
  all: ['financialAdvisorProfile'] as const,
  current: () => [...financialAdvisorProfileKeys.all, 'current'] as const,
  detail: (advisorId: string) =>
    [...financialAdvisorProfileKeys.all, 'detail', advisorId] as const,
};

export const useFinancialAdvisorProfile = (advisorId: string | undefined) =>
  useQuery({
    queryKey: financialAdvisorProfileKeys.detail(advisorId ?? ''),
    queryFn: () => financialAdvisorProfileApi.getProfile(advisorId!),
    enabled: Boolean(advisorId),
  });

export const useCurrentFinancialAdvisorProfile = () =>
  useQuery({
    queryKey: financialAdvisorProfileKeys.current(),
    queryFn: financialAdvisorProfileApi.getMyProfile,
  });

export const useUpdateFinancialAdvisorProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      advisorId,
      payload,
    }: {
      advisorId: string;
      payload: UpdateFinancialAdvisorProfilePayload;
    }) => financialAdvisorProfileApi.updateProfile(advisorId, payload),
    onSuccess: (profile, variables) => {
      queryClient.invalidateQueries({
        queryKey: financialAdvisorProfileKeys.detail(variables.advisorId),
      });
      queryClient.invalidateQueries({ queryKey: financialAdvisorProfileKeys.current() });
      if (profile.id !== variables.advisorId) {
        queryClient.invalidateQueries({
          queryKey: financialAdvisorProfileKeys.detail(profile.id),
        });
      }
    },
  });
};

export const useUpdateCurrentFinancialAdvisorProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateFinancialAdvisorProfilePayload) =>
      financialAdvisorProfileApi.updateMyProfile(payload),
    onSuccess: (profile) => {
      queryClient.invalidateQueries({ queryKey: financialAdvisorProfileKeys.current() });
      queryClient.invalidateQueries({
        queryKey: financialAdvisorProfileKeys.detail(profile.id),
      });
    },
  });
};
